import { useLanguage } from '@vocab/react';
import { useCallback, useRef } from 'react';
import { shallowEqual, useSelector } from 'react-redux';

import { isSubCategoryIdValid } from 'src/app/views/components/JobCategory/utils/isSubCategoryValid';
import { useMutationWithAbort } from 'src/app/views/hooks/useMutationWithAbort';
import { useCreateProductSetForAdPostingMutation } from 'src/graphql/generated';
import type AppState from 'src/types/appState';

import type { UseCreateProductSetInput } from '../useCreateProductSet';

const useCallbacksForCreateProductSetForAdPosting = (
  jobLocation?: string,
): UseCreateProductSetInput => {
  const {
    advertiserNation,
    draftId,
    jobLocationId,
    jobTitle,
    subClassificationId,
    seekAdvertiserId: advertiserId,
  } = useSelector(
    (state: AppState) => ({
      seekAdvertiserId: state.session.seekAdvertiserId,
      jobTitle: state.formData.jobTitle,
      advertiserNation: state.session.hirerAccount.nation,
      jobLocationId: jobLocation ?? state.formData.jobLocationId,
      subClassificationId: state.session.seekSubClassificationId,
      draftId: state.session.draftId,
    }),
    shallowEqual,
  );

  const jobTitleRef = useRef<string>(jobTitle);
  const jobLocationIdRef = useRef<string>(jobLocationId);
  const subClassificationIdRef = useRef<string>(subClassificationId);
  const advertiserNationRef = useRef<string>(advertiserNation);
  const draftIdRef = useRef<string | undefined>(draftId);
  const { locale, language } = useLanguage();

  const isInputSame = useCallback(() => {
    // get previous values to determine any input changes to create product set
    // this is for working out any product price changes given same inputs
    const prevJobTitle = jobTitleRef.current;
    const prevJobLocationId = jobLocationIdRef.current;
    const prevSubClassificationId = subClassificationIdRef.current;
    const prevAdvertiserNation = advertiserNationRef.current;
    const prevDraftId = draftIdRef.current;

    const sameCreateProductSetInput = Boolean(
      prevJobTitle &&
        prevJobLocationId &&
        prevSubClassificationId &&
        jobTitle === prevJobTitle &&
        jobLocationId === prevJobLocationId &&
        subClassificationId === prevSubClassificationId &&
        advertiserNation === prevAdvertiserNation &&
        draftId === prevDraftId,
    );

    // update previous values for next time
    jobTitleRef.current = jobTitle;
    jobLocationIdRef.current = jobLocationId;
    subClassificationIdRef.current = subClassificationId;
    advertiserNationRef.current = advertiserNation;
    draftIdRef.current = draftId;

    return sameCreateProductSetInput;
  }, [jobTitle, jobLocationId, subClassificationId, advertiserNation, draftId]);

  const [createProductSetForAdPostingMutation] =
    useCreateProductSetForAdPostingMutation();

  const createProductSetForAdPostingMutationWithAbort = useMutationWithAbort(
    createProductSetForAdPostingMutation,
  );

  const createProductSetMutation = useCallback(async () => {
    if (
      !(
        advertiserId &&
        jobLocationId &&
        jobTitle &&
        draftId &&
        isSubCategoryIdValid(Number(subClassificationId))
      )
    ) {
      return undefined;
    }

    const response = await createProductSetForAdPostingMutationWithAbort({
      variables: {
        input: {
          advertiserId,
          jobAdDetails: {
            locationId: jobLocationId,
            subClassificationId,
            roleTitle: jobTitle,
          },
          draftId,
          locale: locale || language,
        },
      },
    });
    return response.data?.createProductSetForAdPosting.productSet;
  }, [
    draftId,
    createProductSetForAdPostingMutationWithAbort,
    advertiserId,
    jobLocationId,
    subClassificationId,
    jobTitle,
    locale,
    language,
  ]);

  const getInputsForLogging = useCallback(
    () => ({
      advertiserId,
      advertiserNation,
      locationId: jobLocationId,
      subClassificationId,
      roleTitle: jobTitle,
      draftId: draftId!,
    }),
    [
      advertiserId,
      advertiserNation,
      jobLocationId,
      subClassificationId,
      jobTitle,
      draftId,
    ],
  );

  return { createProductSetMutation, isInputSame, getInputsForLogging };
};

export { useCallbacksForCreateProductSetForAdPosting };
